import { Lightbulb, ChevronDown, ChevronUp, Play, Square } from 'lucide-react';
import React, { useState } from 'react';

const MenuTrivia = ({ trivia, onClick, audio, wpm = 20, farnsworth = null }) => {
    const [isMinimized, setIsMinimized] = useState(() => {
        return localStorage.getItem('trivia_minimized') === 'true';
    });
    const [isPlaying, setIsPlaying] = useState(false);

    // Ref to track play request session
    const playSessionRef = React.useRef(0);

    if (!trivia) return null;

    const toggleMinimize = (e) => {
        e.stopPropagation();
        const newState = !isMinimized;
        setIsMinimized(newState);
        localStorage.setItem('trivia_minimized', newState);
    };

    if (isMinimized) {
        return (
            <div
                onClick={() => setIsMinimized(false)}
                className="bg-surface/60 p-3 rounded-xl shadow-sm border border-border mt-4 mb-4 flex items-center justify-between group cursor-pointer hover:border-primary/50 hover:bg-surface transition-all duration-300"
                title="Click to expand trivia"
            >
                <div className="flex items-center gap-2 text-primary font-bold uppercase text-[10px] tracking-wider">
                    <Lightbulb size={12} className="animate-pulse" />
                    <span className="opacity-70 group-hover:opacity-100 transition-opacity">Daily Trivia Hidden</span>
                </div>
                <button
                    onClick={toggleMinimize}
                    className="p-1 hover:bg-primary/10 rounded-md transition-colors text-muted hover:text-primary"
                >
                    <ChevronDown size={14} />
                </button>
            </div>
        );
    }

    return (
        <div
            onClick={onClick}
            className="bg-surface p-6 rounded-2xl shadow-xl border border-border mt-4 mb-4 relative overflow-hidden group cursor-pointer hover:border-primary/50 hover:shadow-2xl hover:scale-[1.01] transition-all duration-300"
        >
            <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity pointer-events-none">
                <Lightbulb size={48} />
            </div>

            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-primary font-bold uppercase text-xs tracking-wider">
                    <Lightbulb size={14} />
                    <span className="opacity-80">Daily Trivia</span>
                </div>
                <div className="flex items-center gap-1">
                    {audio && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isPlaying) {
                                    audio.stopPlayback();
                                    setIsPlaying(false);
                                } else {
                                    audio.stopPlayback();
                                    setIsPlaying(true);

                                    const currentSession = playSessionRef.current + 1;
                                    playSessionRef.current = currentSession;

                                    // Sanitize text: remove periods and hyphens to avoid confusing Morse audio
                                    const sanitizedText = trivia.text.replace(/[.-]/g, ' ');

                                    audio.playText(sanitizedText, wpm, farnsworth).then(() => {
                                        if (playSessionRef.current === currentSession) {
                                            setIsPlaying(false);
                                        }
                                    });
                                }
                            }}
                            className={`p-1.5 rounded-lg transition-colors relative z-10 ${isPlaying
                                ? 'bg-primary text-primary-content hover:bg-primary-hover shadow-md animate-pulse'
                                : 'hover:bg-primary/10 text-muted hover:text-primary'
                                }`}
                            title={isPlaying ? "Stop (CW)" : "Play in Morse Code"}
                        >
                            {isPlaying ? <Square size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
                        </button>
                    )}
                    <button
                        onClick={toggleMinimize}
                        className="p-1.5 hover:bg-primary/10 rounded-lg transition-colors text-muted hover:text-primary relative z-10"
                        title="Minimize trivia"
                    >
                        <ChevronUp size={16} />
                    </button>
                </div>
            </div>

            <p className="text-main leading-relaxed text-sm group-hover:text-highlight transition-colors pr-8">
                {trivia.text}
            </p>

            <div className="mt-2 text-[10px] text-muted font-bold text-right pt-2 border-t border-border/50 group-hover:border-primary/30 transition-colors">
                Category: {trivia.category}
            </div>
        </div>
    );
};

export default MenuTrivia;
